import { INACCURACY, MIN_CELL_DUR } from 'constants/params';
import { StoryBoard } from 'services/storyboard.service';
import { ClipTransitionAlignment } from 'services/story.service';
import { roundTime } from 'components/app/_helpers/_app.helper';
import { handleSbDelMarkers, sbApi } from 'components/app/editor/_mediator/editor-clips/editor-clips-sb.helper';
import { ClipsMap } from 'components/app/editor/_mediator/editor.slice';

export const markersApi = (markers: MarkerId[], sb: StoryBoard) => ({
  add: (markersToAdd: MarkerId[]) => {
    const cells = markersToAdd.map((markerId) => getCursorCell(markerId, markers));
    return {
      markers: sortMarkers([...markers, ...markersToAdd]),
      storyBoard: sbApi(sb).addMarkers(cells),
    };
  },
  delete: (markersToDell: Index[], clipsMap: ClipsMap) => {
    const markersToDelSet = new Set(markersToDell);
    const storyBoard = handleSbDelMarkers(markersToDell, sb, clipsMap);
    if (storyBoard)
      return {
        markers: markers.filter((_, index) => !markersToDelSet.has(index)),
        storyBoard,
      };
  },
});

export const markersConversion = (
  direction: 'fromBE' | 'toBE',
  markers: MarkerId[],
  transition: Milliseconds,
  transitionAlignment: ClipTransitionAlignment
) => {
  const dir = direction === 'fromBE' ? -1 : 1;
  switch (transitionAlignment) {
    case ClipTransitionAlignment.Leading:
      return shiftMarkers(markers, dir * transition);
    case ClipTransitionAlignment.Centered:
      return shiftMarkers(markers, (dir * transition) / 2);
    default:
      return markers;
  }
};

export const markersInitFromBE = (markers: MarkerId[]) => {
  // TODO: This function should only be applied to a newly created story or after resetting markers
  let res: number[] = markers;
  if (res.length < 2) return res;
  if (markers[0] <= 0) res = markers.filter((num) => num > 0);
  //if (res[0] < averageDuration(res)) res.shift();
  if (res[0] < averageDuration(res)) console.log('res.shift()');
  return res;
};

export const sortMarkers = (markers: MarkerId[]) => {
  return markers.sort((a, b) => a - b);
};

export const getCells = (markers: MarkerId[], trackDuration: Duration) => {
  const duration: Seconds[] = [];
  const width: number[] = [];
  const lastMarker = markers[markers.length - 1];

  duration.push(markers[0]);
  markers.forEach((current, index) => {
    duration.push(roundTime(markers[index + 1] - current));
    if (index === 0) width.push(100 / (trackDuration / current));
    else width.push(100 / (trackDuration / (current - markers[index - 1])));
  });
  duration.pop();
  duration.push(roundTime(trackDuration - lastMarker));
  width.push(100 / (trackDuration / (trackDuration - lastMarker)));
  return { duration, width };
};

/* *** */

export const getCellMarkersTime = (cell: Index, markers: MarkerId[], duration: Duration) => {
  return {
    leftTime: cell === 0 ? 0 : markers[cell - 1],
    rightTime: cell === markers.length ? duration : markers[cell],
  };
};

export const getCellMarkersIndex = (cell: Index, len: number) => {
  return {
    leftIndex: cell === 0 ? null : cell - 1,
    rightIndex: cell === len ? null : cell,
  };
};

export const getCellPayload = (cursorCell: Index, markers: MarkerId[], duration: Duration) => {
  return { ...getCellMarkersTime(cursorCell, markers, duration), ...getCellMarkersIndex(cursorCell, markers.length) };
};

export const getCursorCell = (cursorTime: EditorTime, markers: MarkerId[]): number => {
  const time = cursorTime + INACCURACY;
  let low = 0;
  let high = markers.length - 1;
  let result = markers.length;
  /* binary search */
  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    if (time < markers[mid]) {
      result = mid;
      high = mid - 1;
    } else {
      low = mid + 1;
    }
  }
  return result;
};

export const isSmallRegion = (wsTime: EditorTime, cell: Index, markers: MarkerId[], duration: Duration) => {
  const { leftTime, rightTime } = getCellMarkersTime(cell, markers, duration);
  const leftRange = wsTime - leftTime;
  const rightRange = rightTime - wsTime;
  const error = leftRange <= MIN_CELL_DUR || rightRange <= MIN_CELL_DUR;
  return error;
};

export const isDraggingCell = (time: EditorTime, cell: Index, markers: MarkerId[], duration: Duration) => {
  const { leftTime, rightTime } = getCellMarkersTime(cell, markers, duration);
  return time >= leftTime && time <= rightTime;
};

/* markers config */

export const transitionAlignment = (
  alignment: ClipTransitionAlignment,
  currAlignment: ClipTransitionAlignment,
  transition: Milliseconds,
  markers: MarkerId[]
) => {
  let shiftTime = 0;
  switch (currAlignment) {
    case ClipTransitionAlignment.Trailing:
      shiftTime = alignment === ClipTransitionAlignment.Centered ? -transition / 2 : -transition;
      break;
    case ClipTransitionAlignment.Centered:
      shiftTime = alignment === ClipTransitionAlignment.Trailing ? transition / 2 : -transition / 2;
      break;
    case ClipTransitionAlignment.Leading:
      shiftTime = alignment === ClipTransitionAlignment.Centered ? transition / 2 : transition;
      break;
  }
  return shiftMarkers(markers, shiftTime);
};

export const transitionDuration = (
  duration: Milliseconds,
  currDuration: Milliseconds,
  alignment: ClipTransitionAlignment,
  markers: MarkerId[]
) => {
  if (alignment === ClipTransitionAlignment.Trailing) return markers;
  const diff = currDuration - duration;
  if (alignment === ClipTransitionAlignment.Centered) return shiftMarkers(markers, diff / 2);
  return shiftMarkers(markers, diff);
};

/* *** */

const averageDuration = (arr: number[]) => {
  if (arr.length < 2) return 0;
  let totalDistance = 0;
  for (let i = 1; i < arr.length; i++) {
    totalDistance += arr[i] - arr[i - 1];
  }
  return totalDistance / (arr.length - 1);
};

const shiftMarkers = (markers: MarkerId[], shiftTime: Seconds) => {
  return markers.map((time) => roundTime(time + shiftTime / 1000));
};

/** Marker indexes not in `excluded` (e.g. unselected for keep-selected). */
export const getMarkerIndexesExcluding = (markerCount: number, excluded: Index[]) => {
  const excludedSet = new Set(excluded);
  return Array.from({ length: markerCount }, (_, index) => index).filter((index) => !excludedSet.has(index));
};

/** Remap selection indices by marker time after the marker list mutates. */
export const rematchSelectedMarkersByTime = (
  selectedIndices: Index[],
  oldMarkers: MarkerId[],
  newMarkers: MarkerId[]
): Index[] => {
  const times = selectedIndices.map((index) => oldMarkers[index]);
  return sortMarkers(
    times.map((time) => newMarkers.findIndex((markerId) => markerId === time)).filter((index) => index !== -1)
  );
};
